import { useEffect, useRef, useState } from 'react'

import { Box, FloraInput, Flex } from '@grupoboticario/flora-react'

import HeadingAccelerator from '@/components/HeadingAccelerator/HeadingAccelerator'
import RichTextEditor from '@/components/RichTextEditor/RichTextEditor'
import {
  DETAIL_RULE_TEXT_MAX,
  DETAIL_STEP_TEXT_MAX,
  MAX_DETAIL_RULES,
  MAX_DETAIL_STEPS,
  PAGE_DESCRIPTION_MAX,
  PAGE_TITLE_MAX
} from '@/constants/accelerationForm'
import { sanitizeHtmlForMobileApp } from '@/utils/htmlHelpers/htmlHelpers'

import type { AccelerationContentDetails } from '../AccelerationForm.types'
import { plainTextFromHtml } from '../validation/accelerationFormValidation'
import CharacterCounter from './counter/CharacterCounter'
import FieldTitle from './field-title/FieldTitle'
import { RichTextItemList } from './rich-text/RichTextItemList'

interface StepDetailsProps {
  values: AccelerationContentDetails
  readOnly?: boolean
  onChange: (field: keyof AccelerationContentDetails, value: AccelerationContentDetails[typeof field]) => void
}

const StepDetails = ({ values, readOnly = false, onChange }: StepDetailsProps) => {
  const pageDescriptionRef = useRef<HTMLDivElement>(null)
  const [pageDescriptionCharCount, setPageDescriptionCharCount] = useState(
    () => plainTextFromHtml(values.pageDescription).length
  )

  useEffect(() => {
    if (pageDescriptionRef.current && values.pageDescription) {
      pageDescriptionRef.current.innerHTML = sanitizeHtmlForMobileApp(values.pageDescription)
    }
  }, [values.pageDescription])

  useEffect(() => {
    setPageDescriptionCharCount(plainTextFromHtml(values.pageDescription).length)
  }, [values.pageDescription])

  const handlePageDescriptionBlur = () => {
    if (readOnly) return
    const html = sanitizeHtmlForMobileApp(pageDescriptionRef.current?.innerHTML ?? '')
    onChange('pageDescription', html)
  }

  return (
    <Flex direction="column" gap="$4">
      <Box>
        <FieldTitle isRequired={false}>Título da página</FieldTitle>
        <FloraInput
          data-testid="pageTitleInput"
          id="pageTitle"
          label="Escreva aqui..."
          maxLength={PAGE_TITLE_MAX}
          onChange={(e) => onChange('pageTitle', e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
          required
          size="medium"
          value={values.pageTitle}
        />
        {!readOnly && <CharacterCounter current={values.pageTitle.length} max={PAGE_TITLE_MAX} />}
      </Box>

      <Box>
        <FieldTitle>Descrição</FieldTitle>

        <RichTextEditor
          editorRef={pageDescriptionRef}
          formattingCommands={['bold']}
          maxTextLength={PAGE_DESCRIPTION_MAX}
          minHeight="50px"
          maxHeight="100px"
          isReadOnly={readOnly}
          onBlur={handlePageDescriptionBlur}
          onInput={(text) => setPageDescriptionCharCount(text.length)}
          placeholder="Escreva aqui..."
        />
        {!readOnly && <CharacterCounter current={pageDescriptionCharCount} max={PAGE_DESCRIPTION_MAX} />}
      </Box>

      <HeadingAccelerator title="Etapas" />

      <RichTextItemList
        field="steps"
        items={values.steps}
        readOnly={readOnly}
        maxItems={MAX_DETAIL_STEPS}
        maxTextLength={DETAIL_STEP_TEXT_MAX}
        createIdPrefix="step"
        rowLabel="Etapa"
        addButtonLabel="+ Adicionar etapa"
        onChange={onChange}
      />

      <HeadingAccelerator title="Regras do acelerador" />
      <RichTextItemList
        field="rules"
        items={values.rules}
        readOnly={readOnly}
        maxItems={MAX_DETAIL_RULES}
        maxTextLength={DETAIL_RULE_TEXT_MAX}
        createIdPrefix="rule"
        rowLabel="Regra"
        addButtonLabel="+ Adicionar regra"
        onChange={onChange}
      />
    </Flex>
  )
}

export default StepDetails
